import { useState, type FC } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { Add } from '@mint-lib/icons';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { Button } from '@mint-lib/tw-components';

import DeleteApproverGroup from '../../components/modals/DeleteApproverGroup.jsx';
import useRoleList from '../../hooks/useRoleList.js';
import useUserList from '../../hooks/useUserList.js';
import { type FlowFormInput } from '../../types/singleFlow';
import ApproverGroup from './ApproverGroup.jsx';
import RequesterSection from './RequesterSection.jsx';

type DeleteApproverGroupModalState = {
  isOpened: boolean;
  groupIndex: number;
};

const APPROVER_GROUP_LIMIT_COUNT = 20;

type Props = {
  isFlowInUse: boolean;
};

const FlowCreation: FC<Props> = ({ isFlowInUse }) => {
  const { t } = useTranslation('@myn/approval_v2');
  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');
  const { control } = useFormContext<FlowFormInput>();
  const [deleteApproverGroupModalState, setDeleteApproverGroupModalState] =
    useState<DeleteApproverGroupModalState | null>(null);

  const watchFieldArray = useWatch({ control, name: 'approvers' });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'approvers',
  });
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const roleList = useRoleList();
  const userList = useUserList();

  const isAddGroupButtonDisabled =
    isReadOnly ||
    isFlowInUse ||
    controlledFields.length >= APPROVER_GROUP_LIMIT_COUNT;

  const handleOpenDeleteGroupModal = (groupIndex: number) => {
    setDeleteApproverGroupModalState({
      isOpened: true,
      groupIndex,
    });
  };

  const handleCloseDeleteGroupModal = () => {
    setDeleteApproverGroupModalState(null);
  };

  const handleSubmitDeleteGroupModal = () => {
    if (!deleteApproverGroupModalState) {
      handleCloseDeleteGroupModal();
      return;
    }

    remove(deleteApproverGroupModalState.groupIndex);
    handleCloseDeleteGroupModal();
  };

  const handleAddApproverGroup = () => {
    append({
      entityId: null,
      entityLabel: null,
      type: null,
      operation: 'and',
    });
  };

  return (
    <div className="grid gap-4">
      <RequesterSection allRoles={roleList} allUsers={userList} />
      {controlledFields.map((item, index) => {
        return (
          <ApproverGroup
            key={item.id}
            item={item}
            index={index}
            allRoles={roleList}
            allUsers={userList}
            isFlowInUse={isFlowInUse}
            onRemove={handleOpenDeleteGroupModal}
          />
        );
      })}

      <Button
        aria-label="Add approver group"
        data-dd-action-name="Add an approver group"
        variant="outlined"
        color="primary"
        size="medium"
        type="button"
        onClick={handleAddApproverGroup}
        disabled={isAddGroupButtonDisabled}
      >
        <Add />
        {t('Add approvers')}
      </Button>
      <DeleteApproverGroup
        isOpened={!!deleteApproverGroupModalState?.isOpened}
        onClose={handleCloseDeleteGroupModal}
        onSubmit={handleSubmitDeleteGroupModal}
      />
    </div>
  );
};

export default FlowCreation;
